
/**
 * Switch Component using Radix UI
 * 
 * Usage examples:
 * 
 * // Controlled component
 * <Switch 
 *   checked={isEnabled} 
 *   onCheckedChange={setIsEnabled}
 *   aria-label="Enable notifications"
 * />
 * 
 * // Uncontrolled component
 * <Switch 
 *   defaultChecked={true}
 *   onCheckedChange={(checked) => console.log(checked)}
 * />
 * 
 * // With label
 * <label htmlFor="notifications">
 *   Enable notifications
 *   <Switch id="notifications" />
 * </label>
 */

import React from 'react';
import PropTypes from 'prop-types';

const Switch = React.forwardRef(({
  className, 
  checked, 
  onCheckedChange, 
  disabled = false,
  required = false,
  id,
  name,
  value,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  ...props 
}, ref) => {
  const handleChange = (event) => {
    if (onCheckedChange) {
      onCheckedChange(event.target.checked);
    }
  };
  
  return (
    <div 
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '44px',
        height: '24px',
        margin: '4px'
      }}
      className={className}
    >
      <input
        type="checkbox"
        style={{ 
          position: 'absolute',
          opacity: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          zIndex: 3
        }}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        id={id}
        name={name}
        value={value}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        ref={ref}
        {...props}
      />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: checked ? "hsl(var(--primary))" : "hsl(var(--border-strong))", // primary : border-strong
          borderRadius: '12px',
          transition: 'background-color 0.2s ease-in-out',
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          zIndex: 1
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '2px',
            left: checked ? '22px' : '2px',
            width: '20px',
            height: '20px',
            backgroundColor: 'white',
            borderRadius: '50%',
            transition: 'left 0.2s ease-in-out',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 2
          }}
        />
      </div>
    </div>
  );
});

Switch.displayName = 'Switch';

Switch.propTypes = {
  className: PropTypes.string,
  checked: PropTypes.bool,
  onCheckedChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  'aria-describedby': PropTypes.string,
};

export default Switch;
